import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Film, Clapperboard, Megaphone, Tv2, Sparkles, Tv, CalendarDays, Trophy,
  ArrowRight, AlertTriangle, Archive, Wand2,
} from "lucide-react";
import { toast } from "sonner";

// Convert an existing project to a different `project_type`.
// 3-step: pick → preview (archive + reveal) → confirm.
// Backend never deletes old-shape data; dormant tiles are reachable via
// project settings → Archived workspaces.
export default function ConvertTypeModal({ open, onOpenChange, project, onConverted }) {
  const [types, setTypes] = useState([]);
  const [step, setStep] = useState(0);         // 0 pick · 1 preview
  const [target, setTarget] = useState("");
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) { setStep(0); setTarget(""); setPreview(null); return; }
    api.get("/meta/project-types")
      .then(({ data }) => setTypes((data || []).filter((t) => !t.legacy)))
      .catch(() => {});
  }, [open]);

  const goPreview = async (key) => {
    setTarget(key);
    try {
      const { data } = await api.get(`/projects/${project.id}/type/preview`, {
        params: { target: key },
      });
      setPreview(data);
      setStep(1);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not preview");
    }
  };

  const confirm = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/projects/${project.id}/type`, {
        project_type: target, confirm: true,
      });
      toast.success(data.unchanged
        ? "No change — same project type"
        : `Converted to ${data.type_label}`);
      onOpenChange(false);
      onConverted?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not convert");
    } finally {
      setSaving(false);
    }
  };

  const current = project.project_type;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0a0a0c] border border-white/10 text-white max-w-2xl"
        data-testid="convert-type-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight text-xl">
            {step === 0 ? "Change project type" : "Confirm change"}
          </DialogTitle>
          <DialogDescription className="text-white/55 text-xs">
            {step === 0
              ? "Pick a new format. We keep all your existing data — only the dashboard tiles change."
              : `Converting from ${labelizeType(preview?.current)} to ${labelizeType(preview?.next)}`}
          </DialogDescription>
        </DialogHeader>

        {step === 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-2"
            data-testid="convert-type-grid">
            {types.map((t) => {
              const Icon = ICON[t.key] || Film;
              const isCurrent = t.key === current;
              return (
                <button key={t.key}
                  onClick={() => !isCurrent && goPreview(t.key)}
                  disabled={isCurrent}
                  className={"cs-card rounded-xl p-4 text-left group relative transition-colors " +
                    (isCurrent ? "opacity-50 cursor-not-allowed" : "hover:bg-white/[0.06]")}
                  data-testid={`convert-type-${t.key}`}>
                  {isCurrent && (
                    <span className="absolute top-2 right-2 text-[9px] tracking-[0.22em] uppercase text-white/45"
                      data-testid="convert-type-current-tag">
                      Current
                    </span>
                  )}
                  <div className="w-9 h-9 rounded-lg bg-white/8 flex items-center justify-center text-white/85 group-hover:text-[color:var(--association-theme)]">
                    <Icon size={17} />
                  </div>
                  <div className="mt-3 text-sm font-semibold text-white">{t.label}</div>
                  <div className="mt-1 text-[10px] tracking-[0.18em] uppercase text-white/45">
                    {t.plan.replace("_", " ")} tier
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="space-y-4 mt-2">
            <div className="cs-card rounded-xl p-4 flex items-start gap-3 border border-amber-500/30 bg-amber-500/[0.04]"
              data-testid="convert-type-warning">
              <AlertTriangle size={16} className="text-amber-300 shrink-0 mt-0.5" />
              <div className="text-xs text-amber-100/80 leading-relaxed">
                <b className="text-amber-200">Nothing is deleted.</b> Every workspace
                (Recce, Shot List, Rushes, LUTs, Heatmap, Department grid) stays live.
                Only the dashboard tile set changes.
              </div>
            </div>

            {preview?.revealed_tiles?.length > 0 && (
              <div data-testid="convert-type-revealed">
                <div className="text-[10px] tracking-[0.28em] uppercase text-emerald-300/80 mb-2 flex items-center gap-1.5">
                  <Wand2 size={11} /> New tiles appearing
                </div>
                <ul className="space-y-1.5 text-sm text-white/80">
                  {preview.revealed_tiles.map((t, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <ArrowRight size={12} className="text-emerald-300/70" />
                      {t}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {preview?.dormant_tiles?.length > 0 && (
              <div data-testid="convert-type-dormant">
                <div className="text-[10px] tracking-[0.28em] uppercase text-white/45 mb-2 flex items-center gap-1.5">
                  <Archive size={11} /> Tiles moving to Archive
                </div>
                <ul className="space-y-1.5 text-sm text-white/60">
                  {preview.dormant_tiles.map((t, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <ArrowRight size={12} className="text-white/30" />
                      {t}
                    </li>
                  ))}
                </ul>
                <div className="mt-2 text-[10px] tracking-[0.18em] uppercase text-white/40">
                  Data is preserved. Reachable later via project settings.
                </div>
              </div>
            )}

            <div className="flex items-center justify-between pt-2">
              <button onClick={() => { setStep(0); setPreview(null); }}
                className="text-[10px] tracking-[0.22em] uppercase text-white/55 hover:text-white"
                data-testid="convert-back">
                ← Pick a different format
              </button>
              <Button onClick={confirm} disabled={saving}
                className="btn-themed h-11 px-6 rounded-full text-xs uppercase tracking-[0.25em]"
                data-testid="convert-confirm">
                {saving ? "Converting…" : `Convert to ${preview?.next?.label}`}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const ICON = {
  film:            Film,
  short_film:      Clapperboard,
  ad_film:         Megaphone,
  web_series:      Tv2,
  micro_drama:     Sparkles,
  tv_episodic:     Tv,
  tv_daily_soap:   CalendarDays,
  format_reality:  Trophy,
};

function labelizeType(t) {
  return t?.label || "—";
}
